import { buildExplicitPublicScopeAccess, type PublicScopeAccessSetting } from './wrPublicScope';

type ReportTypeAccessSetting = PublicScopeAccessSetting;

// 閲覧と発行前確認で同じ組織・公開範囲判定を使用する。
export type WrPublicSettingRow = {
  option_id?: unknown;
  department_id?: unknown;
  scope_type?: string | null;
  department_ids?: unknown;
  team_ids?: unknown;
  private_flg?: unknown;
  public_department?: unknown;
  public_team?: unknown;
  /** 設定対象 option の情報 (/get_wr_public_setting が付与)。無効 option の解決に使う。 */
  option_value?: unknown;
  option_label?: unknown;
  option_team_id?: unknown;
  option_department_id?: unknown;
  option_status?: unknown;
};

export type ReportTypeVisibility = 'private' | 'dept' | 'team' | 'public';

export const resolveReportTypeVisibility = (
  row: WrPublicSettingRow | null | undefined
): ReportTypeVisibility => {
  if (!row) return 'private';
  const explicit = buildExplicitPublicScopeAccess(row);
  if (explicit) return explicit.visibility;
  const scopeType = String(row.scope_type ?? '').trim();
  if (scopeType === 'public') return 'public';
  if (scopeType === 'teams') return 'team';
  if (scopeType === 'department' || scopeType === 'departments') return 'dept';
  const privateFlg = Number((row as any)?.private_flg);
  if (!Number.isFinite(privateFlg)) return 'private';
  if (privateFlg === 0) return 'public';
  if (privateFlg === 1) {
    const team = String((row as any)?.public_team ?? '').trim();
    return team ? 'team' : 'private';
  }
  if (privateFlg === 2) {
    const team = String((row as any)?.public_team ?? '').trim();
    if (team) return 'team';
    const dept = String((row as any)?.public_department ?? '').trim();
    if (dept) return 'dept';
    // 一部公開だが公開範囲が空の場合は、実質的に「同一課のみ」の非公開扱いに寄せる
    return 'private';
  }
  return 'private';
};

export const parseWrPublicSettingOptionIds = (
  raw: unknown,
  knownIds?: ReadonlySet<string>
): string[] => {
  if (raw == null) return [];
  const source = String(raw ?? '').trim();
  if (!source) return [];

  if (/^\d+$/.test(source)) {
    return [source];
  }

  const parts = source
    .split(/[,\uFF0C\u3001\/\uFF0F;；\s|]+/)
    .map((v) => String(v ?? '').trim())
    .filter((v) => /^\d+$/.test(v));

  const regexNumbers =
    source.match(/\d+/g)?.map((v) => String(v ?? '').trim()).filter((v) => /^\d+$/.test(v)) ??
    [];

  const joined = source.replace(/[,\uFF0C\u3001\/\uFF0F;；\s|]/g, '').trim();
  const hasKnownIds = Boolean(knownIds && knownIds.size > 0);
  if (!hasKnownIds) {
    const merged = Array.from(new Set([...parts, ...regexNumbers]));
    if (/^\d+$/.test(joined) && !merged.includes(joined)) {
      const looksThousands =
        /^\d{1,3}(,\d{3})+$/.test(source) || /^\d{1,3}(\uFF0C\d{3})+$/.test(source);
      if (looksThousands || merged.length === 0) merged.push(joined);
    }
    return Array.from(new Set(merged));
  }

  const known = knownIds as ReadonlySet<string>;
  const knownParts = Array.from(new Set([...parts, ...regexNumbers])).filter((id) => known.has(id));
  const joinedKnown = /^\d+$/.test(joined) && known.has(joined);
  const looksThousands =
    /^\d{1,3}(,\d{3})+$/.test(source) || /^\d{1,3}(\uFF0C\d{3})+$/.test(source);

  if (knownParts.length > 0 && !joinedKnown) return Array.from(new Set(knownParts));
  if (joinedKnown && knownParts.length === 0) return [joined];
  if (joinedKnown && knownParts.length > 0) {
    // "1,935" のような桁区切り見えのケースは 1935 を優先する
    if (looksThousands) return [joined];
    return Array.from(new Set(knownParts));
  }

  const fallback = Array.from(new Set([...parts, ...regexNumbers]));
  if (/^\d+$/.test(joined) && !fallback.includes(joined)) fallback.push(joined);
  return Array.from(new Set(fallback));
};

export const normalizeScopeCode = (raw: unknown): string =>
  String(raw ?? '')
    .normalize('NFKC')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

export const parseScopeCodes = (raw: unknown): string[] => {
  const source = String(raw ?? '');
  const normalizedSource = source.normalize('NFKC').toUpperCase();

  const bySplit = normalizedSource
    .split(/[,\uFF0C\u3001\/\uFF0F;；|\s]+/)
    .map((v) => normalizeScopeCode(v))
    .filter((v) => v.length > 0);

  const byRegex =
    normalizedSource
      .match(/[A-Z]{1,5}[\s\-_]*\d{1,4}/g)
      ?.map((v) => normalizeScopeCode(v))
      .filter((v) => v.length > 0) ?? [];

  return Array.from(new Set([...byRegex, ...bySplit]));
};

export const buildAccessSettingFromWrPublicSetting = (
  row: WrPublicSettingRow | null | undefined
): ReportTypeAccessSetting => {
  const explicit = buildExplicitPublicScopeAccess(row);
  if (explicit) return explicit;
  return {
    visibility: resolveReportTypeVisibility(row),
    departments: parseScopeCodes((row as any)?.public_department),
    teams: parseScopeCodes((row as any)?.public_team),
  };
};

export const normalizeDepartmentId = (raw: unknown): string | null => {
  const source = String(raw ?? '').trim();
  if (!source) return null;
  if (!/^\d+$/.test(source)) return null;
  const n = Number(source);
  if (!Number.isFinite(n)) return null;
  return String(n);
};

export const resolveDepartmentIdFromValue = (
  raw: unknown,
  deptLabelToIdMap?: Record<string, string>
): string | null => {
  const source = String(raw ?? '').trim();
  if (!source) return null;

  const directId = normalizeDepartmentId(source);
  if (directId) return directId;

  const mappedId = normalizeDepartmentId(deptLabelToIdMap?.[source]);
  if (mappedId) return mappedId;

  const normalizedSource = normalizeScopeCode(source);
  if (!normalizedSource || !deptLabelToIdMap) return null;

  const hit = Object.entries(deptLabelToIdMap).find(
    ([label]) => normalizeScopeCode(label) === normalizedSource
  );
  return normalizeDepartmentId(hit?.[1]);
};

export type UserDepCandidateOptions = {
  deptLabelToIdMap?: Record<string, string>;
  deptIdToLabelMap?: Record<string, string>;
  userDeptIdRaw?: unknown;
};

export const buildUserDepCandidates = (
  userDepRaw: string,
  options?: UserDepCandidateOptions
): string[] => {
  const normalized = new Set<string>();

  const pushCode = (raw: unknown) => {
    const code = normalizeScopeCode(raw);
    if (code) normalized.add(code);
  };

  const pushDeptId = (raw: unknown) => {
    const id = normalizeDepartmentId(raw);
    if (!id) return;
    normalized.add(id);
    const mappedLabel = String(options?.deptIdToLabelMap?.[id] ?? '').trim();
    if (mappedLabel) {
      pushCode(mappedLabel);
    }
  };

  const source = String(userDepRaw ?? '').trim();
  if (source) {
    pushCode(source);

    const embeddedCodes =
      source
        .normalize('NFKC')
        .toUpperCase()
        .match(/[A-Z]{1,5}\d{1,4}/g)
        ?.map((v) => normalizeScopeCode(v))
        .filter((v) => v.length > 0) ?? [];
    embeddedCodes.forEach((code) => normalized.add(code));
  }

  if (!source) {
    pushDeptId(options?.userDeptIdRaw);
  }

  const resolvedFromLabel = resolveDepartmentIdFromValue(source, options?.deptLabelToIdMap);
  if (resolvedFromLabel) {
    pushDeptId(resolvedFromLabel);
  }

  return Array.from(normalized);
};

export const normalizeTeamId = (raw: unknown): string | null => {
  const source = String(raw ?? '').trim();
  if (!source) return null;
  if (!/^\d+$/.test(source)) return null;
  const n = Number(source);
  if (!Number.isFinite(n)) return null;
  return String(n);
};

export type VisibilityDepartmentContext = {
  deptIdToLabelMap?: Record<string, string>;
  deptLabelToIdMap?: Record<string, string>;
  userDeptIdRaw?: unknown;
  userTeamIdRaw?: unknown;
};

export const canAccessPartialRowByScope = (
  setting: ReportTypeAccessSetting,
  userDepRaw: string,
  context?: VisibilityDepartmentContext
): boolean => {
  const departmentScopes = setting.departments ?? [];
  const teamScopes = setting.teams ?? [];

  // 公開範囲に「課」と「チーム」が同時に含まれる場合は OR で判定する。
  // visibility が team でも departments を捨てないことが重要。
  const viewerDepartmentId =
    resolveDepartmentIdFromValue(userDepRaw, context?.deptLabelToIdMap) ??
    normalizeDepartmentId(context?.userDeptIdRaw);
  const viewerTeamId = normalizeTeamId(context?.userTeamIdRaw);

  const matchesDepartmentId =
    viewerDepartmentId != null &&
    departmentScopes.some(
      (departmentId) => normalizeDepartmentId(departmentId) === viewerDepartmentId
    );

  const matchesTeamId =
    viewerTeamId != null &&
    teamScopes.some((teamId) => normalizeTeamId(teamId) === viewerTeamId);

  if (matchesDepartmentId || matchesTeamId) {
    return true;
  }

  // 旧データ互換: public_department に XCF のような課コードが保存されている場合。
  const userDepCandidates = buildUserDepCandidates(userDepRaw, {
    deptLabelToIdMap: context?.deptLabelToIdMap,
    deptIdToLabelMap: context?.deptIdToLabelMap,
    userDeptIdRaw: context?.userDeptIdRaw,
  });

  if (userDepCandidates.length === 0) {
    return false;
  }

  const departmentScopeCandidates = new Set<string>();
  departmentScopes.forEach((raw) => {
    const code = normalizeScopeCode(raw);
    if (code) departmentScopeCandidates.add(code);
  });

  return userDepCandidates.some((dep) => departmentScopeCandidates.has(dep));
};

/** The owning department is never a fallback for a configured limited scope. */
export const canAccessWeeklyReportByScope = (
  setting: ReportTypeAccessSetting,
  userDepRaw: string,
  context: VisibilityDepartmentContext | undefined,
  isOwnDepartment: boolean,
): boolean => {
  if (setting.visibility === 'public') return true;
  if (setting.visibility === 'private') return isOwnDepartment;
  return canAccessPartialRowByScope(setting, userDepRaw, context);
};
